import React from 'react';

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  variant?: 'primary' | 'success' | 'warning';
}

const variantStyles = {
  primary: {
    gradient: 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
    shadow: '0 4px 12px rgba(107, 127, 255, 0.25)',
    shadowHover: '0 6px 16px rgba(107, 127, 255, 0.35)',
  },
  success: {
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    shadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
    shadowHover: '0 6px 16px rgba(16, 185, 129, 0.35)',
  },
  warning: {
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    shadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
    shadowHover: '0 6px 16px rgba(245, 158, 11, 0.35)',
  },
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  variant = 'primary',
}) => {
  const styles = variantStyles[variant];

  const handleAction = () => {
    if (onAction) onAction();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        textAlign: 'center',
        minHeight: '280px',
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: '80px',
          height: '80px',
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: '2px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px',
        }}
      >
        {icon}
      </div>

      {/* Title */}
      <h3
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#0f172a',
          marginBottom: '8px',
        }}
      >
        {title}
      </h3>

      {/* Description */}
      <p
        style={{
          fontSize: '15px',
          color: '#64748b',
          maxWidth: '400px',
          lineHeight: '1.6',
          marginBottom: actionLabel ? '32px' : '0',
        }}
      >
        {description}
      </p>

      {/* Action Button (optional) */}
      {actionLabel && (
        <button
          onClick={handleAction}
          type="button"
          style={{
            background: styles.gradient,
            border: 'none',
            borderRadius: '12px',
            padding: '14px 28px',
            color: 'white',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: styles.shadow,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = styles.shadowHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = styles.shadow;
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
};
